/* eslint-disable jsx-a11y/interactive-supports-focus,jsx-a11y/click-events-have-key-events */
import EVENTS from '@/utils/Constants/Events';
import PropTypes from 'prop-types';
import React from 'react';
import { useTranslation } from 'react-i18next';

// import utils
import { productImageByFormat } from '@/utils/Common/ProductImage';
import { goToPage } from '@/utils/Common/Redirects';
import CONST from '@/utils/Constants/General';
import LINK from '@/utils/Constants/Link';
import CartController from '@/utils/Controllers/CartController';

// import components
import ButtonSocial from '@/components/atoms/ButtonSocial';
import ButtonTextIcon from '@/components/atoms/ButtonTextIcon';
import NumberIncrementer from '@/components/atoms/NumberIncrementer';
import CartPromoBadge from '@/components/molecules/CartPromoBadge';
import Chips from '@/components/molecules/Chips';
import Select from '@/components/molecules/Select';
import CartVatRow from '@/components/organisms/CartVatRow';
import PriceBox from '@/components/organisms/OrderProductBox/PriceBox';

const CartItem = ({ item, addAccessories }) => {
  const itemBundleReferences = item?.bundleReferences;
  const { t } = useTranslation();

  const itemThumbnail = productImageByFormat(
    item.productImages,
    CONST.PRODUCT.IMAGE.FORMAT.THUMBNAIL,
  );

  const updateEntryCart = quantity => {
    DLG.EVE.emit(EVENTS.CART.UPDATE, {
      entry: item,
      quantity,
    });
  };

  const removeItem = () => {
    DLG.EVE.emit(EVENTS.CART.UPDATE, {
      entry: item,
      quantity: 0,
      successMessage: t('cart:cart.entry.removed.success'),
    });
  };

  const editConfigurableProduct = () => {
    const configurationID = item?.configurableProductDataConfigurationId;
    const productCode = item?.productCode;

    goToPage(LINK.PDP_CONFIGURABLE(productCode, configurationID));
  };

  const productConfigurable =
    item?.configurableProductDataConfigurationId &&
    item?.configurableProductDataSnapshot;

  const thumbnailBASE64 = item?.configurableProductDataSnapshot;

  const thumbnailLargeUrl = productConfigurable
    ? thumbnailBASE64
    : itemThumbnail?.l?.url;
  const thumbnailMediumUrl = productConfigurable
    ? thumbnailBASE64
    : itemThumbnail?.m?.url;
  const thumbnailSmallUrl = productConfigurable
    ? thumbnailBASE64
    : itemThumbnail?.s?.url;
  const thumbnailAltText = itemThumbnail?.altText;

  const oldPriceData = {
    value: item.oldPriceValue,
    formattedValue: item.oldPrice,
  };
  const lastLowerPriceData = {
    value: item.lastLowerPrice?.value,
    formattedValue: item.lastLowerPrice?.formattedValue,
  };

  return (
    <div className='dln-cartItem x-cart'>
      <div className='dln-cartItem__content'>
        <div className='dln-cartItem__article'>
          <picture className='dln-image dln-cartItem__picture'>
            <source media='(min-width: 1200px)' srcSet={thumbnailLargeUrl} />
            <source
              media='(min-width: 600px) and (max-width: 1199px)'
              srcSet={thumbnailMediumUrl}
            />
            <a
              href={LINK.PDP(item.productCode)}
              title={item.productName}
              target='_blank'
              rel='noreferrer'
            >
              <img
                className='dln-image__image'
                src={thumbnailSmallUrl}
                alt={thumbnailAltText}
              />
            </a>
          </picture>
          <div className='dln-cartItem__description'>
            <div className='dln-cartItem__titleWrapper'>
              <h3 className='dln-cartItem__title'>{item.productName}</h3>
              <div className='dln-cartItem__topCta'>
                {productConfigurable ? (
                  <ButtonSocial
                    onClick={editConfigurableProduct}
                    text={t('cart:cart.configurable.product.edit')}
                    modifiers={['link', 'icon']}
                    glyph='9'
                  />
                ) : null}
              </div>
            </div>

            <div className='dln-cartItem__price x-cart-item'>
              <div>
                <div className='priceBox'>
                  <PriceBox
                    omnibusScenario={item.omnibusScenario}
                    actualPrice={item.actualPrice}
                    actualPriceValue={item.actualPriceValue}
                    oldPrice={oldPriceData.formattedValue}
                    oldPriceValue={oldPriceData.value}
                    lastLowerPrice={lastLowerPriceData.formattedValue}
                    lastLowerPriceValue={lastLowerPriceData.value}
                  />
                  <CartVatRow vat={item.vat} vatRatio={item.vatratio} />
                </div>
              </div>
              {item.productStock &&
                item.productStock.stockLevelStatus === 'lowStock' && (
                  <div className='dln-cartItem__chips'>
                    <Chips
                      modifiers={['lightGray', 'light']}
                      customHTMLContent={t('cart:cart.stock.text', {
                        stock: item.productStock.stockLevel,
                      })}
                    />
                  </div>
                )}
            </div>
            <CartPromoBadge
              text1={item.entryCouponPromotionMessage}
              text2={item.entryDiscountPromotionMessage}
            />
            <div className='dln-cartItem__quantity'>
              <span className='dln-cartItem__label'>
                {t('cart:cart.popup.qty')}
              </span>
              <NumberIncrementer
                value={item.quantity}
                updateEntry={updateEntryCart}
              />
              <i
                className='dln-icn dln-icn--iconfont'
                onClick={removeItem}
                role='button'
                tabIndex={0}
              >
                X
              </i>
            </div>
            {itemBundleReferences?.length > 0 &&
              itemBundleReferences[0]?.target?.bundleTemplates &&
              itemBundleReferences[0].target.bundleTemplates.length && (
                <div className='dln-cartItem__bundle-references'>
                  <p>{t('cart:cart.bundle.references')}</p>
                  <button
                    type='button'
                    className='del-button'
                    title={t('cart:cart.buy.bundle')}
                    onClick={() => CartController.showAddBundlesModal(item)}
                  >
                    {t('cart:cart.buy.bundle')}
                  </button>
                </div>
              )}
            {item.whatsInTheBoxs && item.whatsInTheBoxs.length > 0 && (
              <div className='dln-cartItem__accessories'>
                <Select
                  values={item.whatsInTheBoxs}
                  id='tools'
                  fieldCode='id'
                  fieldName='title'
                  value=''
                  placeholderLabel={t(
                    'cart:cart.page.accessories.placeholder',
                    {
                      tools: item.whatsInTheBoxs.length,
                    },
                  )}
                  modifiers={['optionsDisabled']}
                />
                {item.accessoriesAvailable && (
                  <ButtonTextIcon
                    onClick={() => addAccessories(item)}
                    text={t('cart:cart.page.accessories.label')}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      {item.whatsInTheBoxs && item.whatsInTheBoxs.length > 0 && (
        <div className='dln-cartItem__accessoriesMobile'>
          <Select
            values={item.whatsInTheBoxs}
            id='tools'
            fieldCode='id'
            fieldName='title'
            value=''
            placeholderLabel={t('cart:cart.page.accessories.placeholder', {
              tools: item.whatsInTheBoxs.length,
            })}
            modifiers={['optionsDisabled']}
          />
          {item.accessoriesAvailable && (
            <ButtonTextIcon
              onClick={() => addAccessories(item)}
              text={t('cart:cart.page.accessories.label')}
            />
          )}
        </div>
      )}
    </div>
  );
};

CartItem.propTypes = {
  item: PropTypes.shape().isRequired,
  addAccessories: PropTypes.func.isRequired,
};

export default CartItem;
